// src/SetupWizard/Step4ContentType.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Paper,
  Slide
} from '@mui/material';
import {
  ListAlt as ListIcon,
  RateReview as ReviewIcon,
  Help as HowToIcon,
  Cases as CaseStudyIcon,
  Newspaper as NewsIcon,
  TipsAndUpdates as TipsIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';

function Step4ContentType({ contentType, contentLength, onChange, onValidityChange }) {
  const [selectedTypes, setSelectedTypes] = useState(() => {
    return Array.isArray(contentType) && contentType.length > 0 ? contentType : [];
  });
  const [length, setLength] = useState(contentLength || '');
  const [showLength, setShowLength] = useState(false);

  useEffect(() => {
    if (selectedTypes.length > 0) {
      onChange({ 
        contentType: selectedTypes, 
        contentLength: length 
      });
    }
  }, []);

  useEffect(() => {
    const isStepComplete = selectedTypes.length > 0 && length !== '';
    onValidityChange(isStepComplete);
  }, [selectedTypes, length, onValidityChange]);

  const contentTypeOptions = [
    {
      type: "How-to guides",
      icon: <HowToIcon />,
      description: "Step-by-step instructions and tutorials"
    },
    {
      type: "Product reviews",
      icon: <ReviewIcon />,
      description: "Detailed analysis and opinions on products"
    },
    {
      type: "Listicles",
      icon: <ListIcon />,
      description: "Engaging numbered lists and collections"
    },
    {
      type: "Case studies",
      icon: <CaseStudyIcon />,
      description: "In-depth analysis of real examples"
    },
    {
      type: "News updates",
      icon: <NewsIcon />,
      description: "Latest industry news and trends"
    },
    {
      type: "Tips & Tricks",
      icon: <TipsIcon />,
      description: "Quick, actionable advice and insights"
    }
  ];

  const contentLengthOptions = [
    {
      label: "Short (500-800 words)",
      value: "500-800",
      description: "Quick reads, perfect for news and lists",
      examples: "News updates, listicles, quick tips"
    },
    {
      label: "Medium (800-1200 words)",
      value: "800-1200",
      description: "Balanced depth for how-to guides and reviews",
      examples: "How-to guides, product reviews, industry insights"
    },
    {
      label: "Long-form (1500-2000 words)",
      value: "1500-2000",
      description: "Comprehensive coverage for case studies",
      examples: "Case studies, detailed guides, in-depth analysis"
    },
    {
      label: "Mixed Lengths",
      value: "500-2000",
      description: "Variety based on topic requirements",
      examples: "Combination of different content types"
    }
  ];

  const handleContentTypeToggle = (type) => {
    let updatedTypes;
    if (selectedTypes.includes(type)) {
      updatedTypes = selectedTypes.filter(t => t !== type);
    } else {
      updatedTypes = [...selectedTypes, type];
    }
    setSelectedTypes(updatedTypes);
    
    if (updatedTypes.length > 0) {
      onChange({ 
        contentType: updatedTypes.filter(t => t && t.trim()),
        contentLength: length 
      });
    }
  };

  const handleLengthChange = (event) => {
    const newLength = event.target.value;
    setLength(newLength);
    
    onChange({ 
        contentType: selectedTypes, 
        contentLength: newLength 
    });
  };

  const handleContinueToLength = () => {
    if (selectedTypes.length > 0) {
      setShowLength(true);
    }
  };

  const handleBackToTypes = () => {
    setShowLength(false);
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 2, position: 'relative', minHeight: '500px' }}>
      <Slide direction="right" in={!showLength} mountOnEnter unmountOnExit>
        <Box>
          <Typography variant="h5" gutterBottom>
            Step 4: Choose Your Content Types
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            Select the types of content you want to create to keep your blog diverse and engaging.
          </Typography>
            
          <Grid container spacing={2}>
            {contentTypeOptions.map(({ type, icon, description }) => (
              <Grid item xs={12} md={6} key={type}>
                <Card 
                  onClick={() => handleContentTypeToggle(type)}
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    transform: selectedTypes.includes(type) ? 'scale(1.02)' : 'scale(1)',
                    border: theme => selectedTypes.includes(type) 
                      ? `2px solid ${theme.palette.primary.main}` 
                      : '2px solid transparent',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: 3
                    }
                  }}
                >
                  <CardContent sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 2,
                    bgcolor: theme => selectedTypes.includes(type) 
                      ? 'action.selected' 
                      : 'background.paper'
                  }}>
                    {icon}
                    <Box>
                      <Typography variant="subtitle1">{type}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {description}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              onClick={handleContinueToLength}
              disabled={selectedTypes.length === 0}
              sx={{ minWidth: 200 }}
            >
              Continue to Content Length
            </Button>
          </Box>
        </Box>
      </Slide>

      <Slide direction="left" in={showLength} mountOnEnter unmountOnExit>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={handleBackToTypes}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Typography variant="h5">
              Choose Content Length
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3 }}>
            Select your typical article length to maintain consistency across your blog.
          </Typography>

          <RadioGroup value={length} onChange={handleLengthChange}>
            <Grid container spacing={2}>
              {contentLengthOptions.map(({ label, value, description, examples }) => (
                <Grid item xs={12} md={6} key={value}>
                  <Paper 
                    elevation={length === value ? 3 : 1}
                    sx={{ 
                      p: 2,
                      border: theme => length === value 
                        ? `2px solid ${theme.palette.primary.main}` 
                        : '2px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        cursor: 'pointer'
                      }
                    }}
                  >
                    <FormControlLabel 
                      value={value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">{label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {description}
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                            Best for: {examples}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%' }}
                    />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        </Box>
      </Slide>
    </Box>
  );
}

export default Step4ContentType;
